/**
 * Preferences (`localStorage`) and per-scene state (`sessionStorage`).
 *
 * Both are allowed to lose data and neither is allowed to corrupt it, which is
 * the whole shape of this file: every read and every write is wrapped, because
 * Web Storage throws in more situations than it is given credit for — a full
 * quota on write, and in Safari with cookies blocked, on *access to the property
 * itself*. A preference that vanishes is an inconvenience; an exception thrown
 * while reading the appearance setting takes the app down before it paints.
 *
 * When storage is unreachable the store keeps its values in memory for the life
 * of the document. That is a real degradation — settings stop surviving a
 * reload — and it is the correct one: the record path is elsewhere and is
 * fail-closed, so nothing durable is riding on this.
 *
 * Keys are namespaced. A dev server hands every project on `localhost` the same
 * origin, and an unprefixed `appearance` key is a collision waiting for the
 * afternoon somebody runs two apps at once.
 */

import type { Cancel, KeyValuePort, SceneStoragePort } from "@ff/domain/ports";

export const PREFERENCE_KEY_PREFIX = "ff.";

export interface StorageLike {
  readonly length: number;
  key(index: number): string | null;
  getItem(key: string): string | null;
  setItem(key: string, value: string): void;
  removeItem(key: string): void;
}

export interface StorageEventLike {
  readonly key: string | null;
  readonly storageArea?: unknown;
}

export interface StorageEventTargetLike {
  addEventListener(type: "storage", listener: (event: StorageEventLike) => void): void;
  removeEventListener(type: "storage", listener: (event: StorageEventLike) => void): void;
}

export interface PreferencesEnvironment {
  /** `undefined` when the browser refuses `localStorage`; memory stands in. */
  readonly storage: StorageLike | undefined;
  /** `undefined` outside a document — nothing can report another tab's writes. */
  readonly eventTarget: StorageEventTargetLike | undefined;
}

export interface SceneStorageEnvironment {
  readonly storage: StorageLike | undefined;
}

/**
 * Reads a Web Storage area without letting its access throw. Safari with
 * cookies blocked throws a `SecurityError` from the property getter, before any
 * method is called.
 */
function resolveStorage(read: () => StorageLike | undefined): StorageLike | undefined {
  try {
    const storage = read();
    if (storage === undefined || storage === null) return undefined;
    // Touching it is the only reliable probe: some engines expose the object and
    // refuse every operation on it.
    void storage.length;
    return storage;
  } catch {
    return undefined;
  }
}

export function browserPreferencesEnvironment(): PreferencesEnvironment {
  return {
    storage: resolveStorage(() => globalThis.localStorage as StorageLike | undefined),
    eventTarget: globalThis.window as StorageEventTargetLike | undefined,
  };
}

export function browserSceneStorageEnvironment(): SceneStorageEnvironment {
  return {
    storage: resolveStorage(() => globalThis.sessionStorage as StorageLike | undefined),
  };
}

/** The in-document stand-in. Same interface, so nothing above it branches. */
class MemoryStorage implements StorageLike {
  readonly #values = new Map<string, string>();

  get length(): number {
    return this.#values.size;
  }

  key(index: number): string | null {
    return [...this.#values.keys()][index] ?? null;
  }

  getItem(key: string): string | null {
    return this.#values.get(key) ?? null;
  }

  setItem(key: string, value: string): void {
    this.#values.set(key, value);
  }

  removeItem(key: string): void {
    this.#values.delete(key);
  }
}

/** The semantics both stores share; only the substrate and the notification differ. */
class WebStringKeyValueStore {
  readonly #storage: StorageLike;

  constructor(storage: StorageLike | undefined) {
    this.#storage = storage ?? new MemoryStorage();
  }

  read(key: string): string | undefined {
    try {
      // `null` means absent; `""` is a value somebody stored, and collapsing the
      // two makes "the user turned this off" indistinguishable from "never set".
      return this.#storage.getItem(PREFERENCE_KEY_PREFIX + key) ?? undefined;
    } catch {
      return undefined;
    }
  }

  write(key: string, value: string): boolean {
    try {
      this.#storage.setItem(PREFERENCE_KEY_PREFIX + key, value);
      return true;
    } catch {
      // A full quota throws. The previous value is untouched, which is the part
      // that matters: a caller can retry a refused write, but it cannot repair
      // one that was clobbered on the way down.
      return false;
    }
  }

  remove(key: string): void {
    try {
      this.#storage.removeItem(PREFERENCE_KEY_PREFIX + key);
    } catch {
      // Removing a key that is not there is a no-op, and so is failing to.
    }
  }

  keys(): readonly string[] {
    const collected: string[] = [];
    try {
      for (let index = 0; index < this.#storage.length; index += 1) {
        const raw = this.#storage.key(index);
        if (raw === null || !raw.startsWith(PREFERENCE_KEY_PREFIX)) continue;
        collected.push(raw.slice(PREFERENCE_KEY_PREFIX.length));
      }
    } catch {
      // An enumeration that fails reports nothing rather than throwing at the
      // caller, which is only ever building a list to display or migrate.
    }
    return collected;
  }

  /** @internal — the subclass needs identity, not access, to filter events. */
  protected ownsStorage(area: unknown): boolean {
    return area === this.#storage;
  }
}

export class BrowserPreferences extends WebStringKeyValueStore implements KeyValuePort {
  readonly #eventTarget: StorageEventTargetLike | undefined;
  #listeners: Array<(key: string) => void> = [];
  #detach: (() => void) | undefined;

  constructor(environment: PreferencesEnvironment = browserPreferencesEnvironment()) {
    super(environment.storage);
    this.#eventTarget = environment.eventTarget;
  }

  subscribe(listener: (key: string) => void): Cancel {
    this.#listeners.push(listener);
    // Attached on the first subscription: the storage event is only interesting
    // to somebody who asked, and a document that never subscribes should not
    // hold a listener on a shared `window`.
    this.#attach();

    return () => {
      const before = this.#listeners.length;
      this.#listeners = this.#listeners.filter((candidate) => candidate !== listener);
      if (before === this.#listeners.length || this.#listeners.length > 0) return;
      this.#detach?.();
      this.#detach = undefined;
    };
  }

  /** Drops the storage listener and every subscriber. */
  dispose(): void {
    this.#listeners = [];
    this.#detach?.();
    this.#detach = undefined;
  }

  #attach(): void {
    const target = this.#eventTarget;
    if (this.#detach !== undefined || target === undefined) return;

    const listener = (event: StorageEventLike): void => {
      // `null` is a `clear()` from another document. There is no key to report
      // and nothing left to enumerate, so it is deliberately ignored rather
      // than guessed at.
      if (event.key === null || !event.key.startsWith(PREFERENCE_KEY_PREFIX)) return;
      // The event fires for both storage areas; only ours is this port's.
      if (event.storageArea != null && !this.ownsStorage(event.storageArea)) return;

      const key = event.key.slice(PREFERENCE_KEY_PREFIX.length);
      for (const subscriber of [...this.#listeners]) subscriber(key);
    };

    target.addEventListener("storage", listener);
    this.#detach = () => {
      target.removeEventListener("storage", listener);
    };
  }
}

/**
 * One tab's scene. There is no subscription because there is nothing to observe:
 * a second tab is a second scene with its own values, which is the entire reason
 * `@SceneStorage` is a different type from a preference rather than an alias
 * for one.
 */
export class BrowserSceneStorage extends WebStringKeyValueStore implements SceneStoragePort {
  constructor(environment: SceneStorageEnvironment = browserSceneStorageEnvironment()) {
    super(environment.storage);
  }
}
